import * as React from "react"
import { Slot } from "radix-ui"

import { cn } from "./cn"

/**
 * A module's own sub-navigation: a row of links, one of them the view on
 * screen. It holds no state — every entry is a link, so the view stays in the
 * URL, a reload lands where it was and the back button works. The row wraps on
 * a narrow screen rather than scrolling sideways, so no entry hides.
 *
 * Name the row (`aria-label`), and mark the entry on screen with `current`:
 *
 *   <NavTabs aria-label="Bereiche">
 *     <NavTab asChild current={path === '/run'}><Link href="/run">Ablauf</Link></NavTab>
 *   </NavTabs>
 *
 * No hooks and no 'use client', so it renders in a server component as well
 * as in a client one; working out which entry is current is the caller's.
 */
function NavTabs({ className, children, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav data-slot="nav-tabs" {...props}>
      <ul
        className={cn(
          "m-0 flex list-none flex-wrap gap-1 p-0",
          "avex:gap-x-1 avex:gap-y-0 avex:border-b avex:border-line",
          className
        )}
      >
        {React.Children.map(children, child => (child === null || child === undefined || child === false ? null : <li className="flex">{child}</li>))}
      </ul>
    </nav>
  )
}

function NavTab({
  className,
  current = false,
  asChild = false,
  ...props
}: React.ComponentProps<"a"> & {
  /** The entry for the view on screen; it gets `aria-current="page"` and the kit's mark. */
  current?: boolean
  /** Render the child (a `next/link` Link) instead of a plain `<a>`, as on `Button`. */
  asChild?: boolean
}) {
  const Comp = asChild ? Slot.Root : "a"
  return (
    <Comp
      data-slot="nav-tab"
      aria-current={current ? "page" : undefined}
      className={cn(
        "inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-lg px-3 text-sm font-medium text-gray-600 no-underline outline-none transition-colors",
        "hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-[3px] focus-visible:ring-ring/50",
        "aria-[current=page]:bg-gray-900 aria-[current=page]:text-white aria-[current=page]:hover:bg-gray-900",
        // AVEX: the design system's underline tabs — a 2px ink line under the
        // entry on screen, sitting on the row's hairline.
        "avex:-mb-px avex:h-10 avex:rounded-none avex:border-b-2 avex:border-transparent avex:bg-transparent avex:px-3 avex:text-ink-secondary",
        "avex:hover:bg-transparent avex:hover:text-ink avex:focus-visible:ring-0 avex:focus-visible:outline-2 avex:focus-visible:outline-offset-[-2px] avex:focus-visible:outline-focus",
        "avex:aria-[current=page]:border-ink avex:aria-[current=page]:bg-transparent avex:aria-[current=page]:font-semibold avex:aria-[current=page]:text-ink avex:aria-[current=page]:hover:bg-transparent",
        className
      )}
      {...props}
    />
  )
}

export { NavTabs, NavTab }
